import React, { useState, useRef, useEffect } from 'react';
import {
  Search, Sparkles, Bell, Calendar, ChevronDown, Check,
  AlertTriangle, Building2, Send,
} from 'lucide-react';
import { getDateFilterLabel, getDateInputMax, relativeTime } from '../utils/format';

const DATE_PRESETS = [
  { key: 'all', label: 'All time', desc: 'Show everything' },
  { key: 'today', label: 'Today', desc: 'Published today' },
  { key: 'yesterday', label: 'Yesterday', desc: 'Previous day' },
  { key: '7d', label: 'Last 7 days', desc: 'Past week' },
  { key: '30d', label: 'Last 30 days', desc: 'Past month' },
];

const NOTIFICATION_META = {
  high_risk: { icon: AlertTriangle, tile: 'bg-[#fae8e7] text-[#bd2f2b]' },
  brief_sent: { icon: Send, tile: 'bg-[#e1f3ea] text-[#117049]' },
  executive_brief_sent: { icon: Sparkles, tile: 'bg-[#e7edfc] text-[#1c4ed8]' },
  company_added: { icon: Building2, tile: 'bg-[#e7edfc] text-[#1c4ed8]' },
  company_results_ready: { icon: Check, tile: 'bg-[#e1f3ea] text-[#117049]' },
};

const NOTIFICATION_ACTIONS = {
  view_high_risk: 'Review high-risk alerts',
  open_email: 'View email activity',
  open_chat: 'Open the chat',
  view_company: 'View company',
};

function NotificationBell({
  notifications = [],
  unreadCount = 0,
  onNotificationAction,
  onMarkAllRead,
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('all');
  const ref = useRef(null);
  const visible = (tab === 'unread' ? notifications.filter(n => n.unread) : notifications).slice(0, 10);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleEscape = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const handleItemClick = (item) => {
    onNotificationAction?.(item);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        className={`relative h-8 w-8 rounded-md border transition flex items-center justify-center text-[#45516a] ${
          open ? 'border-[#cbd3df] bg-[#f6f7fa]' : 'border-transparent hover:bg-[#f6f7fa] hover:border-[#e1e5ec]'
        }`}
        title="Notifications"
      >
        <Bell size={17} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[17px] h-[17px] px-1 rounded-full bg-[#bd2f2b] text-[11px] leading-none font-semibold text-white flex items-center justify-center border-2 border-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[380px] max-w-[calc(100vw-24px)] bg-white border border-[#e1e5ec] rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="px-4 pt-3.5 pb-0 flex items-center justify-between gap-3">
            <h3 className="text-[15px] font-semibold text-[#0d1524]">Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => onMarkAllRead?.()}
                className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[13px] font-medium text-[#1c4ed8] hover:bg-[#e7edfc] transition"
              >
                <Check size={14} />Mark all as read
              </button>
            )}
          </div>

          <div role="tablist" className="flex items-end gap-5 px-4 mt-2 border-b border-[#e1e5ec]">
            {[['all', 'All', notifications.length], ['unread', 'Unread', unreadCount]].map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={`flex items-center gap-1.5 py-2 -mb-px text-[13px] border-b-2 ${
                  tab === key ? 'text-[#1c4ed8] border-[#1c4ed8] font-medium' : 'text-[#45516a] border-transparent hover:text-[#0d1524]'
                }`}
              >
                {label}
                <em className="not-italic text-[11px] font-medium bg-[#f6f7fa] border border-[#e1e5ec] rounded px-[5px] py-px text-[#45516a]">{count}</em>
              </button>
            ))}
          </div>

          <div className="max-h-[360px] overflow-y-auto">
            {visible.length === 0 ? (
              <div className="px-6 py-10 text-center">
                <span className="mx-auto w-10 h-10 rounded-full bg-[#e1f3ea] text-[#117049] grid place-items-center">
                  <Check size={18} />
                </span>
                <p className="mt-3 text-sm font-semibold text-[#0d1524]">
                  {tab === 'unread' ? 'You are all caught up' : 'No notifications yet'}
                </p>
                <p className="mt-1 text-[13px] text-[#76829a]">
                  {tab === 'unread' ? 'New high-risk alerts and sent briefs will show up here.' : 'Alerts and activity will appear here.'}
                </p>
              </div>
            ) : (
              visible.map(item => {
                const meta = NOTIFICATION_META[item.type] || { icon: Bell, tile: 'bg-[#f6f7fa] text-[#45516a]' };
                const Icon = meta.icon;
                const actionLabel = NOTIFICATION_ACTIONS[item.action];
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 border-t border-[#e1e5ec] first:border-t-0 transition hover:bg-[#f6f7fa] ${
                      item.unread ? 'bg-[#f4f7ff]' : ''
                    }`}
                  >
                    <span className={`flex-shrink-0 w-9 h-9 rounded-md grid place-items-center ${meta.tile}`}>
                      <Icon size={16} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-start justify-between gap-2">
                        <b className={`text-[13.5px] leading-snug text-[#0d1524] ${item.unread ? 'font-semibold' : 'font-medium'}`}>{item.title}</b>
                        <span className="flex-none text-xs text-[#76829a] pt-0.5">{relativeTime(item.createdAt)}</span>
                      </span>
                      <span className="block mt-0.5 text-[13px] leading-snug text-[#45516a] line-clamp-2">{item.message}</span>
                      {actionLabel && (
                        <span className="inline-block mt-1.5 text-[13px] font-medium text-[#1c4ed8]">{actionLabel} →</span>
                      )}
                    </span>
                    {item.unread && <span className="w-2 h-2 rounded-full bg-[#1c4ed8] flex-shrink-0 mt-2" aria-label="Unread" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CalendarFilter({ dateFilter, setDateFilter }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleEscape = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const label = getDateFilterLabel(dateFilter);
  const isActive = dateFilter?.preset && dateFilter.preset !== 'all';

  const pick = (preset) => {
    setDateFilter({ preset, customDate: '' });
    setOpen(false);
  };

  const onCustomDate = (e) => {
    const val = e.target.value;
    if (val) {
      setDateFilter({ preset: 'custom', customDate: val });
      setOpen(false);
    }
  };

  const clear = () => {
    setDateFilter({ preset: 'all', customDate: '' });
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-2 h-8 px-3 rounded-md border transition ${
 isActive
 ? 'border-[#1c4ed8] bg-[#e7edfc] text-[#1c4ed8]'
 : 'border-[#cbd3df] bg-white text-[#0d1524] hover:bg-[#f6f7fa]'
 }`}
      >
        <Calendar size={15} className="flex-shrink-0" />
        <span className="text-xs font-semibold whitespace-nowrap max-w-[100px] truncate hidden sm:inline">{label}</span>
        <ChevronDown size={13} className={`flex-shrink-0 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-[#e1e5ec] rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-[#e1e5ec] flex items-center justify-between bg-[#f6f7fa]">
            <div>
              <p className="text-sm font-semibold text-[#0d1524]">Date filter</p>
              <p className="text-xs text-[#5b6780] mt-0.5">Filter alerts by publish date</p>
            </div>
            {isActive && (
              <button
                type="button"
                onClick={clear}
                className="text-[13px] font-medium text-[#5b6780] hover:text-[#45516a] px-3 rounded-md hover:bg-white transition h-8"
              >
                Clear
              </button>
            )}
          </div>
          <div className="py-1.5">
            {DATE_PRESETS.map(p => {
              const selected = dateFilter?.preset === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => pick(p.key)}
                  className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition ${
 selected ? 'bg-[#e7edfc]' : 'hover:bg-[#f6f7fa]'
 }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${selected ? 'font-semibold text-[#1c4ed8]' : 'font-medium text-[#0d1524]'}`}>
                      {p.label}
                    </p>
                    <p className="text-xs text-[#76829a] mt-0.5">{p.desc}</p>
                  </div>
                  {selected && <Check size={15} className="text-[#1c4ed8] flex-shrink-0" />}
                </button>
              );
            })}
          </div>
          <div className="px-4 py-3 border-t border-[#e1e5ec] bg-[#f6f7fa]">
            <label className="text-xs font-semibold text-[#45516a] block mb-2">Custom date</label>
            <input
              type="date"
              value={dateFilter?.preset === 'custom' ? dateFilter.customDate : ''}
              onChange={onCustomDate}
              max={getDateInputMax()}
              className="w-full text-[13px] border border-[#e1e5ec] rounded-md px-3 bg-white focus:outline-none focus:border-[#1c4ed8] h-8"
            />
          </div>
        </div>
      )}
    </div>
  );
}

const HEADER_DATE = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric',
}).format(new Date());

function Header({
  searchQuery, setSearchQuery, handleSearch, openAiPopup,
  notifications = [], notificationUnreadCount = 0,
  onNotificationAction, onMarkAllNotificationsRead,
  dateFilter, setDateFilter, view = 'dashboard',
}) {
  return (
    <header className="sticky top-0 z-40 bg-white border-b border-[#e1e5ec]">
      <div className="px-4 sm:px-6 lg:px-8 h-[54px] flex items-center gap-3">
        <form onSubmit={handleSearch} className="flex-1 min-w-0 max-w-[520px] relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#76829a] pointer-events-none" />
          <input
            type="text"
            placeholder="Search companies, people or intelligence"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full h-[34px] pl-9 pr-3 rounded-md bg-[#f6f7fa] border border-[#e1e5ec] text-[13px] text-[#0d1524] placeholder:text-[#76829a] focus:outline-none focus:border-[#1c4ed8] focus:bg-white transition h-8"
          />
        </form>

        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 ml-auto">
          <span className="hidden md:inline mr-1 text-[13px] font-medium text-[#45516a] whitespace-nowrap">
            {HEADER_DATE}
          </span>
          <NotificationBell
            notifications={notifications}
            unreadCount={notificationUnreadCount}
            onNotificationAction={onNotificationAction}
            onMarkAllRead={onMarkAllNotificationsRead}
          />

          {setDateFilter && (
            <CalendarFilter dateFilter={dateFilter} setDateFilter={setDateFilter} />
          )}

          {view !== 'ai' && (
            <button
              type="button"
              onClick={openAiPopup}
              className="h-8 flex items-center gap-2 px-3 rounded-md border border-[#cbd3df] bg-white hover:bg-[#f6f7fa] text-[#0d1524] text-[13px] font-medium transition"
            >
              <Sparkles size={15} className="text-[#1c4ed8]" />
              <span className="hidden sm:inline">Ask AI</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
